import type { Disposable } from "@/shared/contracts/lifecycle";

/**
 * Plays the Outside Burst effect: a short circular flash that expands from the
 * current Outside-reset presentation target to cover the screen.
 *
 * The effect is purely a notification for Outside-family resets (currently
 * OHov). It is not OHov-specific and carries no pointer events, so it never
 * interferes with gameplay. PHov keeps its own separate effect.
 */
export interface OutsideResetBurstControllerOptions {
  /** Container the burst element is appended to (hidden with its screen). */
  readonly overlayContainer: HTMLElement;
  /** Current viewport geometry and fill color of the presentation target. */
  readonly getOrigin: () => OutsideResetBurstOrigin;
}

export interface OutsideResetBurstOrigin {
  readonly rect: DOMRect;
  readonly fillColor: string;
}

// Safety net so a missed `animationend` (e.g. interrupted reduced-motion run)
// still removes the element. Comfortably longer than the CSS animation.
const BURST_SAFETY_TIMEOUT_MS = 2000;

export class OutsideResetBurstController implements Disposable {
  private readonly overlayContainer: HTMLElement;
  private readonly getOrigin: () => OutsideResetBurstOrigin;
  private activeElement: HTMLDivElement | null = null;
  private safetyTimerId: number | null = null;

  private readonly handleAnimationEnd = (): void => {
    this.clearActiveBurst();
  };

  constructor(options: OutsideResetBurstControllerOptions) {
    this.overlayContainer = options.overlayContainer;
    this.getOrigin = options.getOrigin;
  }

  /**
   * Plays one burst. A re-fire replaces the in-flight burst with the latest
   * one instead of queueing, so rapid Outside resets stay unobtrusive.
   */
  playBurst(): void {
    this.clearActiveBurst();

    const origin = this.getOrigin();
    const originX = origin.rect.left + origin.rect.width / 2;
    const originY = origin.rect.top + origin.rect.height / 2;
    const originRadius = Math.max(origin.rect.width, origin.rect.height) / 2;

    const element = document.createElement("div");
    element.className = "outside-reset-burst";
    element.setAttribute("aria-hidden", "true");
    element.style.setProperty("--outside-reset-burst-x", `${originX}px`);
    element.style.setProperty("--outside-reset-burst-y", `${originY}px`);
    element.style.setProperty(
      "--outside-reset-burst-origin-radius",
      `${originRadius}px`,
    );
    element.style.setProperty(
      "--outside-reset-burst-fill-color",
      origin.fillColor,
    );
    element.addEventListener("animationend", this.handleAnimationEnd);

    this.overlayContainer.append(element);
    this.activeElement = element;
    this.safetyTimerId = window.setTimeout(
      this.handleAnimationEnd,
      BURST_SAFETY_TIMEOUT_MS,
    );
  }

  dispose(): void {
    this.clearActiveBurst();
  }

  private clearActiveBurst(): void {
    if (this.safetyTimerId !== null) {
      window.clearTimeout(this.safetyTimerId);
      this.safetyTimerId = null;
    }
    if (this.activeElement) {
      this.activeElement.removeEventListener(
        "animationend",
        this.handleAnimationEnd,
      );
      this.activeElement.remove();
      this.activeElement = null;
    }
  }
}
